import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setAccessToken, setUser } from '../Redux/UserSlice';
import { toast } from 'react-toastify';

/**
 * Custom hook to handle user inactivity timeout across multiple tabs
 * Gets timeout from API (user data) or defaults to 2 minutes
 * @param {boolean} showWarning - Show warning before logout (default: true)
 * @param {number} warningMinutes - Minutes before timeout to show warning (default: 2 minutes)
 */
const useInactivityTimeout = (showWarning = true, warningMinutes = 1) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const token = useSelector((state) => state?.userData?.access_token);
  const user = useSelector((state) => state?.userData?.user);
  const timeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const warningShownRef = useRef(false);
  const warningToastIdRef = useRef(null); // Store toast ID to dismiss it
  const checkIntervalRef = useRef(null);
  const STORAGE_KEY = 'user_activity_timestamp';
  const LOGOUT_KEY = 'force_logout';

  // Get timeout from API (user data) or default to 2 minutes
  const timeoutMinutes = user?.inactivity_time ?? 5;

  const logout = () => {
    // Dismiss warning toast if it's still showing
    if (warningToastIdRef.current) {
      toast.dismiss(warningToastIdRef.current);
      warningToastIdRef.current = null;
    }
    // Set logout flag in localStorage to notify other tabs
    localStorage.setItem(LOGOUT_KEY, Date.now().toString());
    dispatch(setUser({}));
    dispatch(setAccessToken(""));
    // Navigate directly to home page (public route) - no need to go through AuthRoute
    navigate("/");
    toast.info("You have been logged out due to inactivity.");
  };

  const updateActivityTimestamp = () => {
    if (token) {
      const timestamp = Date.now().toString();
      localStorage.setItem(STORAGE_KEY, timestamp);
      // Clear logout flag if it exists
      localStorage.removeItem(LOGOUT_KEY);
    }
  };

  const dismissWarningToast = () => {
    if (warningToastIdRef.current) {
      toast.dismiss(warningToastIdRef.current);
      warningToastIdRef.current = null;
      warningShownRef.current = false;
    }
  };

  const checkActivity = () => {
    if (!token) {
      return;
    }

    const lastActivity = localStorage.getItem(STORAGE_KEY);
    const forceLogout = localStorage.getItem(LOGOUT_KEY);

    // Check if another tab triggered logout
    if (forceLogout) {
      logout();
      return;
    }

    if (lastActivity) {
      const timeSinceActivity = Date.now() - parseInt(lastActivity);
      const timeoutMs = timeoutMinutes * 60 * 1000;
      const warningMs = (timeoutMinutes - warningMinutes) * 60 * 1000;

      // Check if timeout exceeded
      if (timeSinceActivity >= timeoutMs) {
        logout();
        return;
      }

      // Check if warning should be shown
      if (showWarning && !warningShownRef.current && timeSinceActivity >= warningMs) {
        warningShownRef.current = true;
        // Store the toast ID so we can dismiss it later
        warningToastIdRef.current = toast.warning(
          `You will be logged out in ${warningMinutes} minute(s) due to inactivity.`,
          {
            autoClose: warningMinutes * 60 * 1000,
          }
        );
      }

      // Reset warning if activity detected
      if (timeSinceActivity < warningMs) {
        dismissWarningToast();
      }
    } else {
      // Initialize if not set
      updateActivityTimestamp();
    }
  };

  const resetTimer = () => {
    updateActivityTimestamp();
    // Dismiss warning toast when activity is detected
    dismissWarningToast();
  };

  useEffect(() => {
    // Only activate if user is logged in
    if (!token) {
      // Dismiss any active warning toast
      dismissWarningToast();
      // Clear timers and storage if user is not logged in
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
      }
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(LOGOUT_KEY);
      return;
    }

    // Initialize activity timestamp
    updateActivityTimestamp();

    // List of events to track
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
    ];

    // Add event listeners for user activity
    events.forEach((event) => {
      window.addEventListener(event, resetTimer, true);
    });

    // Listen for storage changes from other tabs
    const handleStorageChange = (e) => {
      if (e.key === STORAGE_KEY) {
        // Activity detected in another tab, dismiss warning
        dismissWarningToast();
      } else if (e.key === LOGOUT_KEY) {
        // Logout triggered from another tab
        logout();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Check activity periodically (every 30 seconds)
    checkIntervalRef.current = setInterval(checkActivity, 30000);

    // Initial check
    checkActivity();

    // Cleanup function
    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, resetTimer, true);
      });
      window.removeEventListener('storage', handleStorageChange);
      
      // Dismiss warning toast on cleanup
      dismissWarningToast();
      
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
      }
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [token, timeoutMinutes, showWarning, warningMinutes, user?.logout_timeout]);

  return { resetTimer };
};

export default useInactivityTimeout;

